import { Component, inject } from '@angular/core';
import { LoadingService } from '../../core/loading.service';

//
// Replaces cgBusy's cg-busy="spinnerGet" directive. cgBusy itself is a dropped AngularJS-only
// dependency, so this is a plain new implementation rather than a port of specific markup.
//
// The original bound cg-busy to the .getMyReport element, so its overlay covered the content area and
// left the top bar alone; this component is rendered in the same place and positions against
// .getMyReport, which site.css gives `position: relative` for exactly that reason.
//
// It no longer serves downloads. isLoading and awaitingCatalog are genuinely blocking - the
// application really is unusable while the catalogue is missing - whereas a download must leave the
// screen operable, since starting a second one is the whole point. This overlay swallowed clicks for
// two seconds after every download, which is what made a second download impossible to start; the
// download feedback is now the non-blocking list in DownloadListComponent.
//
// The live region carries text. It used to wrap a decorative div and nothing else, so it announced
// precisely nothing when it appeared or went - a mechanism present and carrying no information,
// which is a UI-1/UI-2 defect rather than a nicety.
//
@Component({
  selector: 'app-spinner-overlay',
  standalone: true,
  imports: [],
  template: `
    @if (loading.isLoading() || loading.awaitingCatalog())
    {
      <div class="spinnerOverlay" role="status" aria-live="polite">
        <div class="spinner"></div>
        <span class="spinnerLabel">{{ label }}</span>
      </div>
    }
  `,
  styles: [`
    :host { display: contents; }
    .spinnerOverlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(255, 255, 255, 0.6); z-index: 50; }
    .spinner { width: 40px; height: 40px; border: 4px solid #ccc; border-top-color: #67a5db; border-radius: 50%; animation: app-spin 0.8s linear infinite; }
    @keyframes app-spin { to { transform: rotate(360deg); } }

    .spinnerLabel { position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip-path: inset(50%); white-space: nowrap; border: 0; }

    /* Matches .errorOverlay's own dark-mode treatment (menu.css) - dark/near-opaque instead of the light-mode white wash. */
    @media (prefers-color-scheme: dark) {
      .spinnerOverlay { background: rgb(42 42 42 / 90%); }
    }
  `],
})
export class SpinnerOverlayComponent
{
  protected readonly loading = inject(LoadingService);

  //
  // Deliberately not translated through the pipe: this component renders before the catalogue - and
  // therefore before the user's own language - is known, so a resource code here would show as its
  // own name on first paint. It is the one string in the application with that problem.
  //
  protected readonly label = 'Loading';
}
